import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Search, Trash2, User, Phone, Mail, X } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
function authHeader() {
  return { Authorization: `Bearer ${localStorage.getItem("clinic_admin_token")}` };
}

const emptyForm = {
  name: "", phone: "", email: "",
  date_of_birth: "", medical_history: "", insurance_info: ""
};

export default function Patients() {
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  useEffect(() => { fetchPatients(); }, []);

  const fetchPatients = async () => {
    try {
      const { data } = await axios.get(`${API}/patients`, { headers: authHeader() });
      setPatients(data);
    } catch { toast.error("Failed to load patients"); }
    finally { setLoading(false); }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.post(`${API}/patients`, form, { headers: authHeader() });
      toast.success("Patient added");
      setShowModal(false);
      setForm(emptyForm);
      fetchPatients();
    } catch { toast.error("Failed to save patient"); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Remove this patient?")) return;
    try {
      await axios.delete(`${API}/patients/${id}`, { headers: authHeader() });
      toast.success("Patient removed");
      setPatients(prev => prev.filter(p => p.id !== id));
    } catch { toast.error("Failed to delete"); }
  };

  const filtered = patients.filter(p =>
    !search ||
    p.name?.toLowerCase().includes(search.toLowerCase()) ||
    p.phone?.includes(search) ||
    p.email?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="space-y-5" data-testid="patients-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold font-outfit text-white">Patients</h1>
          <p className="text-slate-400 text-sm">{patients.length} registered patients</p>
        </div>
        <button
          data-testid="add-patient-btn"
          onClick={() => setShowModal(true)}
          className="flex items-center gap-2 bg-gradient-to-r from-cyan-500 to-violet-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:from-cyan-400 hover:to-violet-500 transition-all shadow-lg shadow-cyan-500/20"
        >
          <Plus className="w-4 h-4" /> Add Patient
        </button>
      </div>

      {/* Search */}
      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
        <input
          data-testid="patient-search"
          type="text"
          placeholder="Search patients..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="w-full pl-9 pr-4 py-2 bg-[#0A0D14] border border-white/10 rounded-lg text-sm text-white placeholder:text-slate-600 focus:outline-none focus:border-cyan-500/50 focus:ring-1 focus:ring-cyan-500/20 transition-all"
        />
      </div>

      {/* Table */}
      <div className="bg-[#161B27] border border-white/10 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-[#0F1117] border-b border-white/5">
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Patient</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Phone</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Email</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Date of Birth</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Insurance</th>
                <th className="text-right px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {loading ? (
                [...Array(5)].map((_, i) => (
                  <tr key={i} className="animate-pulse">
                    {[...Array(6)].map((_, j) => (
                      <td key={j} className="px-4 py-3">
                        <div className="h-4 bg-white/5 rounded" />
                      </td>
                    ))}
                  </tr>
                ))
              ) : filtered.length === 0 ? (
                <tr>
                  <td colSpan={6} className="text-center py-12 text-slate-500">
                    No patients found
                  </td>
                </tr>
              ) : (
                filtered.map(p => (
                  <tr
                    key={p.id}
                    className="hover:bg-white/5 transition-colors"
                    data-testid={`patient-row-${p.id}`}
                  >
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2.5">
                        <div className="w-8 h-8 rounded-full bg-violet-500/10 border border-violet-500/20 flex items-center justify-center text-violet-400 font-semibold text-xs">
                          {p.name?.charAt(0) || "?"}
                        </div>
                        <span className="font-medium text-white">{p.name}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-slate-400">{p.phone}</td>
                    <td className="px-4 py-3 text-slate-500">{p.email || "—"}</td>
                    <td className="px-4 py-3 text-slate-500">{p.date_of_birth || "—"}</td>
                    <td className="px-4 py-3 text-slate-500 max-w-32 truncate">{p.insurance_info || "—"}</td>
                    <td className="px-4 py-3 text-right">
                      <button
                        data-testid={`delete-patient-${p.id}`}
                        onClick={() => handleDelete(p.id)}
                        className="p-1.5 text-slate-600 hover:text-red-400 hover:bg-red-500/10 rounded-md transition-colors"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add Patient Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-[#161B27] border border-white/10 rounded-2xl shadow-2xl shadow-black/60 w-full max-w-md">
            <div className="flex items-center justify-between px-6 py-4 border-b border-white/5">
              <h2 className="text-lg font-semibold font-outfit text-white">Add Patient</h2>
              <button
                data-testid="close-patient-modal"
                onClick={() => setShowModal(false)}
                className="text-slate-500 hover:text-slate-300 transition-colors rounded-md hover:bg-white/5 p-1"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleSave} className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <label className="text-xs font-medium text-slate-400 block mb-1 uppercase tracking-wider">Full Name *</label>
                  <input
                    data-testid="patient-form-name"
                    required
                    value={form.name}
                    onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
                    className="w-full px-3 py-2 bg-[#0A0D14] border border-white/10 rounded-lg text-sm text-white placeholder:text-slate-600 focus:outline-none focus:border-cyan-500/50 focus:ring-1 focus:ring-cyan-500/20 transition-all"
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-400 block mb-1 uppercase tracking-wider">Phone *</label>
                  <input
                    data-testid="patient-form-phone"
                    required
                    value={form.phone}
                    onChange={e => setForm(p => ({ ...p, phone: e.target.value }))}
                    className="w-full px-3 py-2 bg-[#0A0D14] border border-white/10 rounded-lg text-sm text-white placeholder:text-slate-600 focus:outline-none focus:border-cyan-500/50 focus:ring-1 focus:ring-cyan-500/20 transition-all"
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-400 block mb-1 uppercase tracking-wider">Email</label>
                  <input
                    data-testid="patient-form-email"
                    type="email"
                    value={form.email}
                    onChange={e => setForm(p => ({ ...p, email: e.target.value }))}
                    className="w-full px-3 py-2 bg-[#0A0D14] border border-white/10 rounded-lg text-sm text-white placeholder:text-slate-600 focus:outline-none focus:border-cyan-500/50 focus:ring-1 focus:ring-cyan-500/20 transition-all"
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-400 block mb-1 uppercase tracking-wider">Date of Birth</label>
                  <input
                    data-testid="patient-form-dob"
                    type="date"
                    value={form.date_of_birth}
                    onChange={e => setForm(p => ({ ...p, date_of_birth: e.target.value }))}
                    className="w-full px-3 py-2 bg-[#0A0D14] border border-white/10 rounded-lg text-sm text-white placeholder:text-slate-600 focus:outline-none focus:border-cyan-500/50 focus:ring-1 focus:ring-cyan-500/20 transition-all [color-scheme:dark]"
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-400 block mb-1 uppercase tracking-wider">Insurance</label>
                  <input
                    data-testid="patient-form-insurance"
                    value={form.insurance_info}
                    onChange={e => setForm(p => ({ ...p, insurance_info: e.target.value }))}
                    className="w-full px-3 py-2 bg-[#0A0D14] border border-white/10 rounded-lg text-sm text-white placeholder:text-slate-600 focus:outline-none focus:border-cyan-500/50 focus:ring-1 focus:ring-cyan-500/20 transition-all"
                  />
                </div>
                <div className="col-span-2">
                  <label className="text-xs font-medium text-slate-400 block mb-1 uppercase tracking-wider">Medical History</label>
                  <textarea
                    data-testid="patient-form-history"
                    rows={2}
                    value={form.medical_history}
                    onChange={e => setForm(p => ({ ...p, medical_history: e.target.value }))}
                    className="w-full px-3 py-2 bg-[#0A0D14] border border-white/10 rounded-lg text-sm text-white placeholder:text-slate-600 focus:outline-none focus:border-cyan-500/50 focus:ring-1 focus:ring-cyan-500/20 transition-all resize-none"
                  />
                </div>
              </div>
              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="flex-1 px-4 py-2.5 bg-transparent border border-white/10 rounded-lg text-sm text-slate-400 hover:bg-white/5 hover:text-white transition-colors"
                >
                  Cancel
                </button>
                <button
                  data-testid="save-patient-btn"
                  type="submit"
                  disabled={saving}
                  className="flex-1 px-4 py-2.5 bg-gradient-to-r from-cyan-500 to-violet-600 text-white rounded-lg text-sm font-medium hover:from-cyan-400 hover:to-violet-500 disabled:opacity-50 transition-all shadow-lg shadow-cyan-500/20"
                >
                  {saving ? "Saving..." : "Add Patient"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
